import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { usePractice } from '../context/PracticeContext';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import NHLogo from './NHLogo';
import { useNavAnalytics } from '../hooks/useNavAnalytics';
import { isProdEnv, isNavV2Enabled, setNavV2Enabled } from '../utils/env';
import { resolveNavForRole, ADMIN_NAV_CONFIG, TESTING_ITEMS } from '../config/navigation';
import '../styles/Sidebar.css';

function Sidebar({ isAdminView = false, collapsed, onToggleCollapse, isMobile = false, mobileOpen = false, onMobileClose }) {
  const location = useLocation();
  const { selectedPracticeId } = usePractice();
  const { hasPermission, user } = useAuth();
  const { track } = useNavAnalytics();
  const [aiQueuePending, setAiQueuePending] = useState(0);
  const navV2 = isNavV2Enabled();
  const activeRole = user?.activeContext?.role || user?.user?.role || null;

  const loadAiQueueCount = useCallback(async () => {
    if (!selectedPracticeId || isAdminView) return;
    try {
      let total = 0;
      const result = await api.getAiQueue(selectedPracticeId, { status: 'pending' });
      if (result.success && result.summary) {
        total += Number(result.summary.pending) || 0;
      }
      try {
        const cbResult = await api.getCallbackTasks(selectedPracticeId, { status: 'pending', limit: 1 });
        if (cbResult.success && cbResult.stats) {
          total += Number(cbResult.stats.total_pending) || 0;
        }
      } catch {}
      setAiQueuePending(total);
    } catch (err) {
      // Silently fail — badge just won't show
    }
  }, [selectedPracticeId, isAdminView]);

  useEffect(() => {
    loadAiQueueCount();
    const interval = setInterval(loadAiQueueCount, 30000);
    return () => clearInterval(interval);
  }, [loadAiQueueCount]);

  const practiceNavSections = [
    {
      label: 'Overview',
      items: [
        { path: '/practice-view', label: 'Dashboard', icon: 'home', exact: true, permission: 'reports:dashboard' },
        { path: '/practice-view/roi-dashboard', label: 'ROI Dashboard', icon: 'trending', permission: 'reports:dashboard' },
        { path: '/practice-view/operations-readiness', label: 'Pre-Visit Readiness', icon: 'alert', permission: 'reports:dashboard' },
        { path: '/practice-view/ai-queue', label: 'AI Review Hub', icon: 'sparkle', badge: aiQueuePending, permission: 'sms:ai_queue' },
      ]
    },
    {
      label: 'Care Hub',
      items: [
        { path: '/practice-view/patients', label: 'Patient Directory', icon: 'users', permission: 'patients:view' },
        { path: '/practice-view/care-hub', label: 'Care Hub', icon: 'calendar', permission: 'appointments:view' },
        { path: '/practice-view/patient-flow', label: 'Patient Flow', icon: 'activity', permission: 'flow:view' },
        { path: '/practice-view/ambient-scribe', label: 'Ambient Scribe', icon: 'mic', permission: 'scribe:view' },
        { path: '/practice-view/reports', label: 'Insights', icon: 'chart', permission: 'reports:dashboard' },
        { path: '/practice-view/ai-insights', label: 'AI Predictions', icon: 'zap', permission: 'ai:insights' },
      ]
    },
    {
      label: 'Outreach',
      items: [
        { path: '/practice-view/campaigns', label: 'Campaigns', icon: 'megaphone', permission: 'campaigns:view' },
        { path: '/practice-view/waitlist', label: 'Waitlist', icon: 'clock', permission: 'waitlist:view' },
        { path: '/practice-view/notification-center', label: 'Notifications', icon: 'bell', permission: 'notifications:view' },
        { path: '/practice-view/messaging', label: 'Messaging', icon: 'chat', permission: 'sms:view' },
        { path: '/practice-view/voice-ai', label: 'Phone Outreach', icon: 'phone', permission: 'voice:call' },
        { path: '/practice-view/reputation', label: 'Reputation', icon: 'star', permission: 'reputation:view' },
      ]
    },
    {
      label: 'Billing',
      items: [
        { path: '/practice-view/revenue-flow', label: 'RevenueFlow', icon: 'trending', permission: 'billing:revenue_flow' },
        { path: '/practice-view/billing-escalations', label: 'Escalations', icon: 'alert', permission: 'billing:escalations' },
        { path: '/practice-view/insurance-change-requests', label: 'Insurance Changes', icon: 'shield', permission: 'intake:manage' },
        { path: '/practice-view/payment-settings', label: 'Payment Settings', icon: 'creditcard', permission: 'billing:configure' },
      ]
    },
    {
      label: 'Testing',
      items: [
        { path: '/practice-view/testbed', label: 'TestBed', icon: 'beaker', permission: 'campaigns:run' },
        { path: '/practice-view/ehr-live-test', label: 'EHR Live Test', icon: 'zap', permission: 'ehr:sync' },
        { path: '/practice-view/payment-test', label: 'Payment Test', icon: 'creditcard', permission: 'billing:configure' },
      ]
    },
    {
      label: 'Compliance',
      items: [
        { path: '/practice-view/compliance', label: 'Compliance', icon: 'shield', permission: 'compliance:view' },
      ]
    },
    {
      label: 'Settings',
      items: [
        { path: '/practice-view/practice-settings', label: 'Practice Settings', icon: 'settings', permission: 'settings:view' },
        { path: '/practice-view/tag-management', label: 'Tags & Labels', icon: 'tag', permission: 'tags:manage' },
        { path: '/practice-view/activity', label: 'Activity Log', icon: 'clock', permission: 'reports:activity' },
        { path: '/practice-view/logs', label: 'System Logs', icon: 'file', permission: 'logs:practice' },
      ]
    }
  ];

  const adminNavSections = [
    {
      label: 'Overview',
      items: [
        { path: '/admin', label: 'Dashboard', icon: 'home', exact: true },
      ]
    },
    {
      label: 'Management',
      items: [
        { path: '/admin/practice-groups', label: 'Practice Hub', icon: 'folder' },
        { path: '/admin/users', label: 'Users', icon: 'users' },
        { path: '/admin/identity', label: 'Identity & SSO', icon: 'shield' },
        { path: '/admin/audit-log', label: 'Audit Log', icon: 'clock' },
        { path: '/admin/hipaa-audit-log', label: 'HIPAA Audit', icon: 'file' },
        { path: '/admin/logs', label: 'Platform Logs', icon: 'file' },
        { path: '/admin/baa', label: 'BAA Tracking', icon: 'shield' },
        { path: '/admin/compliance', label: 'Compliance Engine', icon: 'shield' },
        { path: '/admin/migration', label: 'V1 Migration', icon: 'folder' },
        { path: '/admin/phone-management', label: 'Phone Management', icon: 'phone' },
      ]
    }
  ];

  // Filter nav items based on user permissions (admin sections are already super_admin-only via route gating)
  // Phase 1: hide "Testing" in prod. Phase 2: swap in role-first v2 config when navV2 flag is on.
  let rawSections;
  if (isAdminView) {
    rawSections = navV2 ? ADMIN_NAV_CONFIG : adminNavSections;
  } else if (navV2) {
    rawSections = resolveNavForRole(activeRole);
    // In v2, restore Test bench visibility for practice/clinical admins on non-prod.
    // super_admin still uses the Admin drawer for these; practice_admin used to reach
    // TestBed / EHR Live Test / Payment Test via the v1 Testing section.
    const nonSuperAdmin = activeRole === 'practice_admin' || activeRole === 'clinical_admin';
    if (!isProdEnv() && nonSuperAdmin) {
      rawSections = [...rawSections, { label: 'Test bench', items: TESTING_ITEMS }];
    }
  } else {
    rawSections = practiceNavSections;
  }

  const sections = rawSections
    .filter(section => !(section.label === 'Testing' && isProdEnv()))
    .map(section => ({
      ...section,
      items: section.items
        .filter(item => !item.permission || hasPermission(item.permission))
        // v2's AI Review Hub gets the same live badge count as v1
        .map(item => item.path === '/practice-view/ai-queue' ? { ...item, badge: aiQueuePending } : item)
    }))
    .filter(section => section.items.length > 0);

  const isActive = (item) => {
    if (item.exact) return location.pathname === item.path;
    return location.pathname.startsWith(item.path);
  };

  const sidebarClasses = [
    'sidebar',
    collapsed ? 'sidebar--collapsed' : '',
    isAdminView ? 'sidebar--admin' : '',
    isMobile && mobileOpen ? 'sidebar--mobile-open' : '',
  ].filter(Boolean).join(' ');

  const handleNavClick = (item, section) => {
    try {
      track('nav_click', {
        item: item.path,
        item_label: item.label,
        section: section.label,
        view: isAdminView ? 'admin' : 'practice',
        role: activeRole,
        nav_v2: navV2,
      });
    } catch {}
    if (isMobile && onMobileClose) onMobileClose();
  };

  return (
    <>
      {isMobile && mobileOpen && (
        <div className="sidebar__overlay" onClick={onMobileClose} />
      )}
      <aside className={sidebarClasses}>
        <div className="sidebar__brand">
          {isMobile && (
            <button className="sidebar__close-btn" onClick={onMobileClose} aria-label="Close menu">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
          {!collapsed && (
            isAdminView
              ? <span className="sidebar__brand-text">NH Admin</span>
              : <NHLogo size={28} light />
          )}
          {collapsed && !isMobile && <NHLogo size={24} showText={false} />}
        </div>

        <nav className="sidebar__nav">
          {sections.length === 0 && (!collapsed || isMobile) && (
            <div className="sidebar__empty-state">
              <p className="sidebar__empty-title">Your workspace is still being set up</p>
              <p className="sidebar__empty-hint">
                Ask your practice admin to grant access, or try <kbd>Ctrl+K</kbd> to search for a page.
              </p>
            </div>
          )}
          {sections.map((section) => (
            <div key={section.label} className="sidebar__section">
              {(!collapsed || isMobile) && (
                <div className="sidebar__section-label">{section.label}</div>
              )}
              {section.items.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  end={item.exact}
                  data-nav={item.path.split('/').pop()}
                  data-nav-item={item.path}
                  data-nav-section={section.label}
                  className={({ isActive: navActive }) =>
                    `sidebar__item ${navActive || isActive(item) ? 'sidebar__item--active' : ''}`
                  }
                  title={collapsed && !isMobile ? item.label : undefined}
                  onClick={() => handleNavClick(item, section)}
                >
                  <SidebarIcon name={item.icon} />
                  {(!collapsed || isMobile) && <span className="sidebar__item-label">{item.label}</span>}
                  {item.badge > 0 && (
                    <span className={`sidebar__badge ${collapsed && !isMobile ? 'sidebar__badge--collapsed' : ''}`}>
                      {item.badge > 99 ? '99+' : item.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        {!isMobile && (
          <div className="sidebar__footer">
            {(activeRole === 'super_admin' || !isProdEnv()) && (
              <button
                className={`sidebar__nav-toggle ${navV2 ? 'sidebar__nav-toggle--v2' : ''}`}
                onClick={() => { setNavV2Enabled(!navV2); window.location.reload(); }}
                title={navV2 ? 'Switch back to Nav v1' : 'Try new Nav v2 layout'}
              >
                {(!collapsed || isMobile) ? (
                  <span>Nav <strong>{navV2 ? 'v2' : 'v1'}</strong></span>
                ) : (
                  <span>{navV2 ? 'v2' : 'v1'}</span>
                )}
              </button>
            )}
            <button
              className="sidebar__collapse-btn"
              onClick={onToggleCollapse}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <CollapseIcon collapsed={collapsed} />
            </button>
          </div>
        )}
      </aside>
    </>
  );
}

function SidebarIcon({ name }) {
  const icons = {
    home: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0a1 1 0 01-1-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 01-1 1" />
      </svg>
    ),
    users: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
      </svg>
    ),
    shield: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    chart: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 20V10M12 20V4M6 20v-6" />
      </svg>
    ),
    megaphone: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
      </svg>
    ),
    chat: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
      </svg>
    ),
    phone: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z" />
      </svg>
    ),
    star: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
      </svg>
    ),
    mic: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
        <path d="M19 10v2a7 7 0 01-14 0v-2" />
        <line x1="12" y1="19" x2="12" y2="23" />
        <line x1="8" y1="23" x2="16" y2="23" />
      </svg>
    ),
    sparkle: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2L15 9L22 12L15 15L12 22L9 15L2 12L9 9L12 2Z" />
      </svg>
    ),
    settings: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
      </svg>
    ),
    file: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
    tag: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
    ),
    clock: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <polyline points="12 6 12 12 16 14" />
      </svg>
    ),
    folder: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" />
      </svg>
    ),
    building: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
        <line x1="9" y1="6" x2="9" y2="6.01" />
        <line x1="15" y1="6" x2="15" y2="6.01" />
        <line x1="9" y1="10" x2="9" y2="10.01" />
        <line x1="15" y1="10" x2="15" y2="10.01" />
        <line x1="9" y1="14" x2="9" y2="14.01" />
        <line x1="15" y1="14" x2="15" y2="14.01" />
        <line x1="9" y1="18" x2="15" y2="18" />
      </svg>
    ),
    beaker: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 3h6M10 3v6.5L5 19a2 2 0 0 0 1.7 3h10.6a2 2 0 0 0 1.7-3L14 9.5V3" />
        <path d="M8.5 14h7" />
      </svg>
    ),
    calendar: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="3" y1="10" x2="21" y2="10" />
      </svg>
    ),
    zap: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
      </svg>
    ),
    trending: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </svg>
    ),
    alert: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" />
        <line x1="12" y1="17" x2="12.01" y2="17" />
      </svg>
    ),
    creditcard: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </svg>
    ),
    activity: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    bell: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
        <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      </svg>
    ),
  };

  return (
    <span className="sidebar__icon">
      {icons[name] || icons.file}
    </span>
  );
}

function CollapseIcon({ collapsed }) {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      {collapsed ? (
        <polyline points="9 18 15 12 9 6" />
      ) : (
        <polyline points="15 18 9 12 15 6" />
      )}
    </svg>
  );
}

export default Sidebar;
